'use client';

import { useState, useEffect } from 'react';
import { analytics, AnalyticsEvent } from '@/lib/analytics';
import Icon from '@/components/ui/AppIcon';

export default function EventsTable() {
  const [events, setEvents] = useState<AnalyticsEvent[]>([]);
  const [filter, setFilter] = useState<string>('all');

  useEffect(() => {
    loadEvents();
  }, [filter]);

  const loadEvents = () => {
    const allEvents = analytics.getAllEvents();
    const filtered = filter === 'all' ? allEvents : allEvents.filter((e) => e.type === filter);
    setEvents(filtered.slice(-50).reverse());
  };

  const formatTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getEventIcon = (type: string): string => {
    switch (type) {
      case 'page_view':
        return 'EyeIcon';
      case 'shipment_search':
        return 'MagnifyingGlassIcon';
      case 'quote_request':
        return 'DocumentTextIcon';
      case 'conversion':
        return 'CheckCircleIcon';
      case 'user_action':
        return 'CursorArrowRaysIcon';
      default:
        return 'BellIcon';
    }
  };

  const getEventColor = (type: string): string => {
    switch (type) {
      case 'page_view':
        return 'text-primary';
      case 'shipment_search':
        return 'text-secondary';
      case 'quote_request':
        return 'text-accent';
      case 'conversion':
        return 'text-success';
      default:
        return 'text-muted-foreground';
    }
  };

  return (
    <div className="bg-card rounded-xl p-6 shadow-card border border-border">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Recent Events</h3>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-4 py-2 bg-background border border-border rounded-lg text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">All Events</option>
          <option value="page_view">Page Views</option>
          <option value="shipment_search">Shipment Searches</option>
          <option value="quote_request">Quote Requests</option>
          <option value="conversion">Conversions</option>
          <option value="user_action">User Actions</option>
        </select>
      </div>

      {events.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Type</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Details</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <Icon name={getEventIcon(event.type)} size={16} className={getEventColor(event.type)} />
                      <span className="text-sm font-medium text-foreground capitalize">
                        {event.type.replace('_', ' ')}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm text-muted-foreground">
                      {JSON.stringify(event.data).substring(0, 100)}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm text-muted-foreground">{formatTimestamp(event.timestamp)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 text-muted-foreground">
          <Icon name="InboxIcon" size={48} className="mx-auto mb-4" />
          <p>No events recorded yet</p>
        </div>
      )}
    </div>
  );
}